'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, generateRandomAvatarColor } from '@/lib/utils';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Loader2 } from 'lucide-react';
import { useUser } from '@/hooks/useUser';
import { useDebounce } from '@/components/ui/multi-select';

type UserInfo = {
  id: string;
  name: string;
  email: string;
  username: string;
  profilePicture: string;
};

interface MentionSuggestionProps {
  items: any[];
  command: (item: any) => void;
  query: string;
}

export const MentionSuggestion = forwardRef(
  (props: MentionSuggestionProps, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Create a debounced function to update the query
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetQuery = useDebounce(props.query, 300);

    // Use the API hook to fetch users with the debounced query
    const { getAllUsersApi } = useUser({
      search: debouncedSetQuery,
      loadUsers: true,
    });

    const { data, isLoading, error } = getAllUsersApi;

    // Get users from API response
    const users = data?.data || [];

    const selectItem = (index: number) => {
      const item = users[index];
      if (item) {
        props.command(item);
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (users.length === 0) return false;

        if (event.key === 'ArrowUp') {
          setSelectedIndex((selectedIndex + users.length - 1) % users.length);
          return true;
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex((selectedIndex + 1) % users.length);
          return true;
        }

        if (event.key === 'Enter') {
          if (users.length > 0) {
            selectItem(selectedIndex);
          }
          return true;
        }

        return false;
      },
    }));

    useEffect(() => {
      setSelectedIndex(0);
    }, [debouncedSetQuery]);

    // Show loading state
    if (isLoading) {
      return (
        <div className="bg-white rounded-md shadow-md border p-4 w-[300px] flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-gray-500 mr-2" />
          <span>Loading users...</span>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="bg-white rounded-md shadow-md border p-4 w-[300px] text-red-500">
          Failed to load users
        </div>
      );
    }

    // Show empty state
    if (users.length === 0) {
      return (
        <div className="bg-white rounded-md shadow-md border p-4 w-[300px] text-gray-500">
          No users found
        </div>
      );
    }

    return (
      <div className="bg-white rounded-md shadow-md border overflow-hidden max-h-[200px] overflow-y-auto z-40 w-[300px]">
        {users.map((user, index) => {
          // Get initials for avatar fallback
          const initials =
            user.name
              ?.split(' ')
              ?.map((part: string) => part[0])
              ?.join('')
              ?.toUpperCase()
              ?.substring(0, 2) ?? 'UN';

          const { backgroundColor, textColor } = generateRandomAvatarColor(
            user.name
          );

          return (
            <button
              key={user.id}
              className={cn(
                'flex items-center gap-2 w-full p-2 text-left hover:bg-slate-100',
                selectedIndex === index && 'bg-slate-100'
              )}
              onClick={() => selectItem(index)}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.profilePicture} alt={user.name} />
                <AvatarFallback
                  style={{
                    backgroundColor: backgroundColor,
                    color: textColor,
                  }}
                  className="text-sm font-semibold"
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-sm">
                {user.name ?? user.username ?? user.email}
              </span>
            </button>
          );
        })}
      </div>
    );
  }
);

MentionSuggestion.displayName = 'MentionSuggestion';
